import { createId } from '@paralleldrive/cuid2'
import type { HarmonyPackage } from '@rhythmic/db/types'
import type { Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'
import { getPlanCollection } from '../../getCollections'

import { normaliseString } from '../../harmonyPlan/useHarmonyPlanContext.planMutations.shared'

const logger = useRhythmicLogger('useHarmonyPlanContext:createPlan')

export interface CreateHarmonyPlanPayload {
  title: string
  teamId?: string | null
}

export interface CreateHarmonyPlanContext {
  workspaceId: string
  teamId: string
}

function validateParams(payload: CreateHarmonyPlanPayload, context: CreateHarmonyPlanContext) {
  const { teamId, workspaceId } = context

  if (!teamId) {
    const error = new Error('Team context is not ready')
    throw error
  }
  if (!workspaceId) {
    const error = new Error('Workspace context is not ready')
    throw error
  }

  const trimmedTitle = normaliseString(payload.title)
  if (!trimmedTitle) {
    const error = new Error('Plan title is required')
    logger.error('Unable to create harmony plan: missing title')
    throw error
  }

  return {
    trimmedTitle,
    teamId,
    workspaceId,
  }
}

export default function handleCreateHarmonyPlan(
  payload: CreateHarmonyPlanPayload,
  context: CreateHarmonyPlanContext,
): Transaction {
  try {
    const { trimmedTitle, teamId, workspaceId } = validateParams(payload, context)

    if (!trimmedTitle) {
      const error = new Error('Plan title is required')
      logger.error('create harmony plan blocked: missing title')
      throw error
    }

    const newPackage: HarmonyPackage = {
      id: `plan_${createId()}`,
      workspace_id: workspaceId,
      team_id: teamId,
      title: trimmedTitle,
      created_at: new Date(),
      updated_at: new Date(),
    }

    const planCollection = getPlanCollection(workspaceId)
    const transaction = planCollection.insert(newPackage)
    logger.debug('Created new harmony plan', { transaction, newPackage })
    return transaction as Transaction
  } catch (error) {
    logger.error('Failed to create harmony plan', { error })
    if (error instanceof Error) throw error
    throw new Error('Failed to create harmony plan')
  }
}
